/**
 * Tour analytics (spec 25 section 7.4): Umami events, and only where a
 * tracker exists. The app image carries no tracker; the demo's vhost injects
 * one into its own pages, so `window.umami` is present on demo.ladeway.com and
 * absent everywhere else, and this is a no-op everywhere else by construction.
 *
 * Every call is wrapped: analytics is never the visitor's problem.
 */
const SLUG = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/** The outreach campaign that brought this visitor, if the URL carried a valid one. */
export function campaignFrom(search) {
  try {
    const tag = (new URLSearchParams(search || '').get('utm_campaign') || '').toLowerCase();
    return tag.length <= 60 && SLUG.test(tag) ? tag : null;
  } catch (e) {
    return null;
  }
}

export function track(name, data = {}) {
  try {
    if (typeof window === 'undefined' || !window.umami || !window.umami.track) return false;
    window.umami.track(name, data);
    return true;
  } catch (e) {
    return false;
  }
}

/*
 * Spec 33 section 5.7: one event per thing a walkthrough step can do, named
 * after what Pendo and Chameleon count (seen, advanced, dismissed, timed out,
 * completed), so step-level drop-off is "started minus completed" per step.
 * The story tour keeps its own demo-tour-* names; dashboards already read them.
 */
export const WALKTHROUGH_EVENTS = Object.freeze({
  start: 'demo-walkthrough-start', // { walkthrough, entry: link|menu|handoff|resume }
  stepStart: 'demo-step-start', // { walkthrough, step, n }
  stepComplete: 'demo-step-complete', // { walkthrough, step, mode: shown|self|read }
  stepSkip: 'demo-step-skip', // { walkthrough, step }
  stepTimeout: 'demo-step-timeout', // { walkthrough, step, waited: target|server }
  stepRefused: 'demo-step-refused', // { walkthrough, step, status }
  takeover: 'demo-autoplay-takeover', // { walkthrough, step }
  branch: 'demo-branch', // { walkthrough, step, choice }
  complete: 'demo-walkthrough-complete', // { walkthrough, seconds }
  abandon: 'demo-walkthrough-abandon', // { walkthrough, step, reason: exit|left|error }
  explore: 'demo-explore-item', // { item }
});

/** Numbers and short strings only: nothing but what the walkthrough itself named. */
export function walkthroughData(data = {}) {
  const out = {};
  for (const [key, value] of Object.entries(data || {})) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    else if (typeof value === 'string') out[key] = value.slice(0, 60);
  }
  return out;
}

/** @param {keyof typeof WALKTHROUGH_EVENTS} event */
export function trackWalkthrough(event, data = {}) {
  const name = WALKTHROUGH_EVENTS[event];
  if (!name) return false;
  return track(name, walkthroughData(data));
}
